// TypeScript
import dayjs from "dayjs";

import { D2DOrderRow } from "./components/D2DActionMenu/D2DActionMenu";
import {
  D2DRequestsListResponse,
  D2DROrderApiItem,
} from "../../../api/d2d/d2dApiInterfaces";

export interface GetD2DOrdersParams {
  baseUrl: string;
  accessToken: string;
  accountId: string;
  page: number;
  pageSize: number;
  searchText?: string;
}

export interface GetD2DOrdersResponse {
  data: D2DOrderRow[];
  totalCount: number;
}

export const mapApiResponseToRow = (
  apiItem: D2DROrderApiItem
): D2DOrderRow => ({
  id: apiItem.id,
  orderNumber: apiItem.orderNumber,
  dealerReference: apiItem.dealerReference,
  partNumber: apiItem.partNumber,
  requestedQuantity: apiItem.requestedQuantity,
  acceptedQuantity: apiItem.acceptedQuantity,
  dateRequested: apiItem.dateRequested,
  timeToRespond: apiItem.timeToRespond ?? "",
  status: apiItem.status,
  lastChangedBy: apiItem.lastChangedBy,
  rejectionType: apiItem.rejectionType ?? undefined,
  rejectionTrigger: apiItem.rejectionTrigger ?? undefined,
  carrierName: apiItem.carrierName ?? "",
  trackingNumber: apiItem.trackingNumber ?? "",
  weight: apiItem.weight ?? "",
  buyerAccountId: apiItem.buyerAccountId,
});

export const sortD2DRequestRows = (rows: D2DOrderRow[]): D2DOrderRow[] => {
  const statusRank = (row: D2DOrderRow) =>
    String(row.status ?? "")
      .toUpperCase()
      .trim() === "NEW"
      ? 0
      : 1;

  const dateValue = (row: D2DOrderRow) => {
    const parsed = dayjs(row.dateRequested ?? "");
    return parsed.isValid() ? parsed.valueOf() : Number.NEGATIVE_INFINITY;
  };

  return [...rows].sort((a, b) => {
    const rankDiff = statusRank(a) - statusRank(b);
    if (rankDiff !== 0) return rankDiff;
    return dateValue(b) - dateValue(a);
  });
};

export const mapResponseToState = (response: D2DRequestsListResponse) => {
  const d2dOrders = response.data.d2dOrders ?? [];
  const rows: D2DOrderRow[] = sortD2DRequestRows(
    d2dOrders.map(mapApiResponseToRow)
  );
  const totalCount = response.data.totalCount ?? d2dOrders.length;

  return { rows, totalCount };
};

/**
 * Fetches D2D requests from backend.
 * Shape matches AllOrders `getAllOrders` style.
 *
 * Currently unused but kept for potential direct fetch usage.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getD2DRequests = async (
  params: GetD2DOrdersParams
): Promise<GetD2DOrdersResponse> => {
  const { baseUrl, accessToken, accountId, page, pageSize, searchText } =
    params;

  const query = new URLSearchParams({
    accountId,
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (searchText && searchText.trim()) {
    query.append("search", searchText.trim());
  }

  const response = await fetch(
    `${baseUrl}/v1/d2d-order/orders?${query.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        "Accept-Language": dayjs.tz?.guess?.() ?? "en",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch D2D requests");
  }

  const body = await response.json();

  const requests: D2DROrderApiItem[] = body.requests ?? [];

  return {
    data: requests.map(mapApiResponseToRow),
    totalCount: body.totalCount ?? 0,
  };
};
